/**
 * Datenmodell des Lernpfads.
 *
 * Neun Aufgabentypen, bewusst festgelegt: nur was die Engine rendern UND
 * bewerten kann, darf die KI erzeugen. Ein zehnter Typ wäre schnell im Prompt
 * ergänzt, aber ohne Renderer und ohne Bewertungsregel landet er als leere
 * Karte im Quiz – deshalb ist die Liste geschlossen und der Generator prüft
 * jede Aufgabe dagegen (siehe `sanitize` in generate.ts).
 *
 *   info      Wissenskarte – erklärt, fragt nicht (Teach-First)
 *   single    eine richtige Antwort
 *   multiple  mehrere richtige Antworten
 *   fill      Lückentext, Bausteine per Klick/Drag in die Lücken
 *   match     Paare zuordnen
 *   calc      Zahl eintippen (mit Einheit und Toleranz)
 *   error     fehlerhafte Zeile in einem Text finden, danach Korrektur sehen
 *   code      freie Eingabe im Monospace-Feld (Code, Formel, Definition)
 *   table     Tabelle vervollständigen (je Zeile aus einer Liste wählen)
 *
 * Jede Aufgabe trägt ihren Anforderungsbereich (AFB 1–3), bis zu drei
 * gestufte Tipps und eine Referenz auf die Fundstelle im eigenen Material.
 */

import type { IconName } from "@/lib/icons";

export type Afb = 1 | 2 | 3;

export const AFB_LABEL: Record<Afb, string> = {
  1: "AFB I · Reproduktion",
  2: "AFB II · Zusammenhänge",
  3: "AFB III · Bewerten & Übertragen",
};

/**
 * Verweis auf die Stelle im eigenen Material, aus der die Aufgabe stammt.
 * `sourceId` ist die ID eines `SourceBlock` (siehe services/source.service.ts) –
 * damit lässt sich später zur Mitschrift-Stelle bzw. PDF-Seite springen.
 */
export interface Reference {
  sourceId: string;
  label: string;
  /** Wörtliches Zitat aus der Quelle (kurz), belegt die Herkunft. */
  snippet?: string;
  page?: number;
}

interface QBase {
  afb: Afb;
  explain?: string;
  reference?: Reference;
}

export interface InfoQ extends QBase {
  type: "info";
  title: string;
  /** Absätze; Markdown-fett und `code` sind erlaubt. */
  body: string[];
  /** Optionaler abgesetzter Block (Merksatz, Formel, Codebeispiel). */
  box?: string;
}

export interface SingleQ extends QBase {
  type: "single";
  question: string;
  options: string[];
  correct: number;
}

export interface MultipleQ extends QBase {
  type: "multiple";
  question: string;
  options: string[];
  correct: number[];
}

export interface FillQ extends QBase {
  type: "fill";
  question: string;
  /** Text mit Platzhaltern `{{1}}`, `{{2}}` … in aufsteigender Reihenfolge. */
  text: string;
  /** Lösung je Platzhalter, in derselben Reihenfolge. */
  blanks: string[];
  /** Zusätzliche falsche Bausteine für die Auswahlleiste. */
  distractors?: string[];
}

export interface MatchQ extends QBase {
  type: "match";
  question: string;
  /** [links, rechts] – 3 bis 5 Paare. */
  pairs: [string, string][];
}

export interface CalcQ extends QBase {
  type: "calc";
  question: string;
  answer: number;
  unit?: string;
  /** Erlaubte Abweichung in Prozent (Standard 1 %). */
  tolerance?: number;
  /** Rechenweg, erscheint als Erklärung. */
  solution?: string;
}

export interface ErrorQ extends QBase {
  type: "error";
  question: string;
  /** Zeilen des fehlerhaften Texts. */
  lines: string[];
  /** Index der Zeile mit dem Fehler. */
  badLine: number;
  /** Dieselbe Zeile richtig – wird nach dem Prüfen eingeblendet. */
  fixedLine: string;
}

export interface CodeQ extends QBase {
  type: "code";
  question: string;
  /** Vorbelegung des Eingabefelds. */
  starter?: string;
  /** Musterlösung. */
  expected: string;
  /** Weitere gültige Schreibweisen. */
  accept?: string[];
}

export interface TableQ extends QBase {
  type: "table";
  question: string;
  /** Spaltenüberschriften [vorgegeben, auszuwählen]. */
  columns: [string, string];
  rows: { given: string; correct: string }[];
  /** Auswahlliste je Zeile (enthält alle richtigen plus Ablenker). */
  options: string[];
}

export type Question =
  | InfoQ
  | SingleQ
  | MultipleQ
  | FillQ
  | MatchQ
  | CalcQ
  | ErrorQ
  | CodeQ
  | TableQ;

export type QuestionType = Question["type"];

export const QUESTION_TYPES: QuestionType[] = [
  "info",
  "single",
  "multiple",
  "fill",
  "match",
  "calc",
  "error",
  "code",
  "table",
];

/** Was eine Aufgabe zum Verständnis-Score beiträgt (schwer zählt mehr). */
export const AFB_WEIGHT: Record<Afb, number> = { 1: 1, 2: 2, 3: 3 };

/* ------------------------------- Ergebnis ------------------------------- */

export type Verdict = "correct" | "partial" | "wrong" | "info";

export interface CheckResult {
  verdict: Verdict;
  /** Was der Nutzer im Feedback-Balken liest. */
  title: string;
  detail?: string;
}

/* -------------------------------- Lektion -------------------------------- */

export interface LessonPlan {
  sources: string[];
  content: string[];
  kinds: string[];
}

export interface Lesson {
  id: string;
  topic: string;
  sequence: string;
  seq_pos: number;
  position: number;
  title: string;
  goal: string;
  icon: IconName;
  afb: Afb;
  questions: Question[];
  plan?: LessonPlan;
  source_hash: string;
  done: boolean;
  best_pct: number;
  xp: number;
  last_done: string;
}

/** Eine Sequenz ist nur ein Name plus ihre Lektionen (siehe SCHEMA.md). */
export interface Sequence {
  name: string;
  pos: number;
  lessons: Lesson[];
}

/** Farbe eines Sequenz-Abschnitts im Pfad – zyklisch, wie bei Duolingo. */
export const SEQ_COLORS = ["green", "blue", "purple", "gold", "orange"] as const;
export type SeqColor = (typeof SEQ_COLORS)[number];

export function seqColor(pos: number): SeqColor {
  return SEQ_COLORS[((pos % SEQ_COLORS.length) + SEQ_COLORS.length) % SEQ_COLORS.length];
}
